import { ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ObjectUtils } from '../../../../core/utils/ObjectUtils';
import { ShapeComponent } from '../../components/shape/shape.component';
import { ShapeKeys } from '../enums/ShapeKeys';
import { IElement } from '../interfaces/IElement';
import { ISeat } from '../interfaces/ISeat';
import { IShape } from '../interfaces/IShape';
import { FlattenSeat } from '../types';
import { SeatsPlanService } from './SeatsPlanService';
@Injectable({
  providedIn: 'root'
})
export class ShapesService {

	public readonly shapeComponents = signal<ComponentRef<ShapeComponent>[]>([]);

	private readonly _seatsPlanService = inject(SeatsPlanService);
	private readonly _shapes = this._seatsPlanService.shapes;
	private readonly _injector = inject(EnvironmentInjector);

	private readonly _keysMap = new Map<ShapeKeys, { attribute: string, dataset: string }>([
		[ShapeKeys.SHAPE_TYPE, { attribute: 'data-shape-type', dataset: 'shapeType' }],
		[ShapeKeys.STATE, { attribute: 'data-state', dataset: 'state' }],
		[ShapeKeys.SEAT_ID, { attribute: 'data-seat-id', dataset: 'seatId' }],
	]);

	/**
	 * Creates a shape element
	 * @param dataObject - seat or base element
	 */
	public createShapeElement(dataObject: IElement | ISeat): Observable<HTMLElement> {
		const flattenedDataObject = ObjectUtils.flat(dataObject) as FlattenSeat;
		const shape = this._shapes().find(x => x.id === flattenedDataObject.shapeId) ?? null;

		if (!shape) {
			return of(null);
		}

		const componentRef = this.createAndGetShapeComponentRef(
			shape,
			flattenedDataObject
		);

		this.shapeComponents.update(val => [...val, componentRef]);

		return new Observable((observer) => {
			setTimeout(() => {
				this._seatsPlanService.roomTemplate().insert(componentRef.hostView);
				componentRef.changeDetectorRef.detectChanges();
				observer.next(componentRef.location.nativeElement);
				observer.complete();
			});
		});
	}

	public getAttribute(key: ShapeKeys): string {
		return this._keysMap.get(key)?.attribute || '';
	}

	public getDataset(key: ShapeKeys): string {
		return this._keysMap.get(key)?.dataset || '';
	}

	/**
	 * Get & set SHAPE_TYPE
	 */
	public getShapeType(el: HTMLElement): string {
		return el.dataset[this.getDataset(ShapeKeys.SHAPE_TYPE)] || '';
	}

	public setShapeType(el: HTMLElement, type: string): void {
		el.dataset[this.getDataset(ShapeKeys.SHAPE_TYPE)] = type;
	}

	public getHTMLElement(id: number, container?: HTMLElement): HTMLElement {
		return (container ?? this._seatsPlanService.roomRef()).querySelector<HTMLElement>(
			`[${this.getAttribute(ShapeKeys.SEAT_ID)}="${id}"]`
		) || null;
	}

	public getShapeComponent(id: number): ShapeComponent {
		return this.shapeComponents().find(x => x.instance.seatId === id)?.instance;
	}

	private createAndGetShapeComponentRef(shape: IShape, dataObject: FlattenSeat): ComponentRef<ShapeComponent> {
		const componentRef = createComponent(ShapeComponent, {
			environmentInjector: this._injector
		});

		Object.assign(componentRef.instance, {
			shape,
			dataObject
		});

    return componentRef;
  }
}
